<template>
  <div class="QuickBuyItem py-16 flex justify-between gap-12 border-t border-border-subtle">
    <component
      :is="categoryCode === 'Samples' ? 'div' : 'nuxt-link'"
      :to="categoryCode === 'Samples' ? undefined : productUrl(product.url)"
      class="block relative basis-50 shrink-0 cursor-pointer"
      @click="emit('close')"
    >
      <nuxt-img
        :src="product.mainImage"
        provider="norce"
        preset="plpimage"
      />
      <div class="imageOverlay absolute top-0 left-0 w-full h-full" />
    </component>
    <component
      :is="categoryCode === 'Samples' ? 'div' : 'nuxt-link'"
      :to="categoryCode === 'Samples' ? undefined : productUrl(product.url)"
      class="block basis-full cursor-pointer"
      @click="emit('close')"
    >
      <div class="type-headline-sm">{{ product.variantName }}</div>
      <div class="text-sm text-text-subtle mt-2 empty:hidden">
        {{ product.shortDescription }}
      </div>
      <div class="text-sm mt-2">
        <div
          class="StockStatus rounded-full w-8 h-8 mr-8 border inline-block"
          :class="[ `bg-${stockLabel.color}`, `border-${stockLabel.borderColor}` ]"
        />
        <span class="type-sm" v-html="stockLabel.text" />
      </div>
    </component>

    <div class="text-right whitespace-nowrap type-headline-sm">
      <div v-if="displayedPrice" v-html="displayedPrice" />
    </div>
    <div class="basis-40 shrink-0">
      <button
        v-if="categoryCode === 'Samples' && userStore.isLoggedIn"
        class="btn btn--wIcon w-40"
        :disabled="stockLabel.disableBuyButton"
        :class="{ 'loading': loading }"
        @click="addToCart"
      >
        <img src="/icons/cart-inv.svg" class="inline-block w-16 h-16">
      </button>
      <button
        v-else-if="userStore.isLoggedIn"
        class="w-40 h-40 bg-bg-inverse flex items-center justify-center"
        :disabled="stockLabel.disableBuyButton"
        @click="openQuickbuy"
      >
        <img src="/icons/cart-add-inv.svg" class="inline-block w-16 h-16">
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { ProductModel } from '~/models/product';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import { useCartStore } from '~/store/cart';
import { StockLabel } from '~/composeables/useStockStatus';
import { useUiStore } from '~/store/ui';

const { productUrl } = useLinkReplacer();
const userStore = useUserStore();
const globalContent = useGlobalContentStore();
const cartStore = useCartStore();
const uiStore = useUiStore();

// Emit definitions
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'addingToCart'): void;
  (e: 'addToCart'): void;
}>();

const props = defineProps<{
  product: ProductModel,
  stockLabel: StockLabel,
  categoryCode?: string |null,
}>();

const addToCart = async() => {
  loading.value = true;
  await cartStore.updateCart(props.product.partNo, 1, -1, true);
  loading.value = false;
  emit('close');
};

// Optional loading state
const loading = ref(false);

// Compute the displayed price for this product
const displayedPrice = computed(() => {
  const price = userStore.getPrice(props.product.partNo);
  if (price?.status === 'ok') {
    let output = `<p class="pt-8">${
      globalContent.getDisplayIncVat
        ? price.price?.priceDisplay
        : price.price?.priceBeforeVatDisplay
    }</p>`;

    if (price.price?.isOnSale) {
      output = `<p>${
        globalContent.getDisplayIncVat
          ? price.price?.priceDisplay
          : price.price?.priceBeforeVatDisplay
      }</p>
      <p class="line-through text-text-subtle">${
  globalContent.getDisplayIncVat
    ? price.price?.priceBeforeDiscountDisplay
    : price.price?.priceBeforeDiscountBeforeVatDisplay
}</p>`;
    }
    return output;
  }
  return null;
});

const openQuickbuy = async() => {
  try {
    loading.value = true;
    cartStore.setCurrentlyBuying(props.product, false, false);
    uiStore.setShowQuickBuy(true);
  } catch (error) {
    console.error('Error in openQuickbuy:', error);
  } finally {
    loading.value = false;
    emit('close');
  }
};
</script>

<style scoped lang="postcss">
</style>
